/**
 * As chamadas ao Last.fm e o resgate de capa na iTunes — o
 * `music_sync_service.dart`.
 *
 * A chave vem do servidor (`ajustes.lastfmKey`), e não do binário: quem chama
 * passa a chave, e sem chave não há chamada nenhuma. As duas APIs respondem com
 * `access-control-allow-origin: *`, então o navegador fala direto com elas —
 * não há proxy no meio, e o mesmo arquivo serve ao site e ao celular.
 */

import { limparCapaDoLastfm } from './estado';

const BASE = 'https://ws.audioscrobbler.com/2.0/';

/**
 * Quem o Last.fm respondeu 404 uma vez.
 *
 * Um nome de usuário errado nos ajustes viraria uma consulta a cada 30 s para
 * sempre. Depois do primeiro 404 o nome entra aqui e as próximas rodadas o
 * pulam. Some quando o app reinicia, que é quando alguém teria corrigido o
 * nome nos ajustes.
 */
const inexistentes = new Set<string>();

export const usuarioInexistente = (usuario: string): boolean => inexistentes.has(usuario);

/** `http.get(...).timeout(...)`: o Dart aborta, o `fetch` também precisa. */
async function pegar(url: string, milissegundos: number): Promise<unknown | null> {
  const freio = new AbortController();
  const relogio = setTimeout(() => freio.abort(), milissegundos);
  try {
    const resposta = await fetch(url, { signal: freio.signal });
    if (!resposta.ok) return null;
    return await resposta.json();
  } catch {
    return null;
  } finally {
    clearTimeout(relogio);
  }
}

/** O mesmo, mas distinguindo o 404 do resto para alimentar `inexistentes`. */
async function pegarDoLastfm(
  url: string,
  usuario: string | null,
  milissegundos = 10000,
): Promise<unknown | null> {
  const freio = new AbortController();
  const relogio = setTimeout(() => freio.abort(), milissegundos);
  try {
    const resposta = await fetch(url, { signal: freio.signal });
    if (resposta.status === 404 && usuario) inexistentes.add(usuario);
    if (!resposta.ok) return null;
    return await resposta.json();
  } catch {
    return null;
  } finally {
    clearTimeout(relogio);
  }
}

const lista = (valor: unknown): Record<string, unknown>[] => {
  if (Array.isArray(valor)) return valor as Record<string, unknown>[];
  // O Last.fm entrega um objeto só, sem array, quando há exatamente um item.
  if (valor && typeof valor === 'object') return [valor as Record<string, unknown>];
  return [];
};

/** `user.getrecenttracks`: o que a pessoa ouviu por último. */
export async function faixasRecentes(
  usuario: string,
  chave: string,
  quantas = 1,
): Promise<Record<string, unknown>[]> {
  if (!chave || !usuario || inexistentes.has(usuario)) return [];
  const url =
    `${BASE}?method=user.getrecenttracks&user=${encodeURIComponent(usuario)}` +
    `&api_key=${encodeURIComponent(chave)}&format=json&limit=${quantas}`;
  const dados = (await pegarDoLastfm(url, usuario)) as Record<string, unknown> | null;
  const recentes = dados?.recenttracks as Record<string, unknown> | undefined;
  return lista(recentes?.track);
}

/** `user.gettoptracks`: os mais ouvidos de sempre (`period=overall`). */
export async function faixasMaisTocadas(
  usuario: string,
  chave: string,
  quantas = 10,
  periodo = 'overall',
): Promise<Record<string, unknown>[]> {
  if (!chave || !usuario || inexistentes.has(usuario)) return [];
  const url =
    `${BASE}?method=user.gettoptracks&user=${encodeURIComponent(usuario)}` +
    `&period=${periodo}&limit=${quantas}&api_key=${encodeURIComponent(chave)}&format=json`;
  const dados = (await pegarDoLastfm(url, usuario)) as Record<string, unknown> | null;
  const tops = dados?.toptracks as Record<string, unknown> | undefined;
  return lista(tops?.track);
}

/** Acentos, hífens e "(Remastered)" fora: só letras e números, minúsculos. */
const normalizar = (valor: string): string => valor.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/**
 * A capa que o `user.gettoptracks` não trouxe.
 *
 * O Last.fm devolve a lista de mais tocados sem imagem com muita frequência, e
 * uma lista de dez quadrados vazios não diz nada. Primeiro tenta o
 * `track.getinfo` do próprio Last.fm; se ele também vier sem capa, cai na busca
 * da iTunes, que tem quase tudo e é aberta.
 */
export async function capaDaFaixa(
  artista: string,
  faixa: string,
  chave: string,
  mbid?: string | null,
): Promise<string | null> {
  const doLastfm = await capaPeloLastfm(artista, faixa, chave, mbid);
  if (doLastfm) return doLastfm;
  return capaPelaItunes(artista, faixa);
}

async function capaPeloLastfm(
  artista: string,
  faixa: string,
  chave: string,
  mbid?: string | null,
): Promise<string | null> {
  if (!chave) return null;
  const identificacao = mbid
    ? `&mbid=${encodeURIComponent(mbid)}`
    : `&artist=${encodeURIComponent(artista)}&track=${encodeURIComponent(faixa)}`;
  const url = `${BASE}?method=track.getinfo&api_key=${encodeURIComponent(chave)}&format=json${identificacao}`;

  const dados = (await pegarDoLastfm(url, null)) as Record<string, unknown> | null;
  const info = dados?.track as Record<string, unknown> | undefined;
  const album = info?.album as Record<string, unknown> | undefined;
  const imagens = album?.image;
  if (!Array.isArray(imagens) || imagens.length === 0) return null;

  let escolhida = imagens[imagens.length - 1] as Record<string, unknown>;
  for (const img of imagens) {
    if (img && typeof img === 'object' && (img as Record<string, unknown>).size === 'extralarge') {
      escolhida = img as Record<string, unknown>;
      break;
    }
  }
  const texto = escolhida?.['#text'];
  return limparCapaDoLastfm(typeof texto === 'string' ? texto : null);
}

async function capaPelaItunes(artista: string, faixa: string): Promise<string | null> {
  // Duas tentativas: com o artista junto, que erra menos, e só o título, para
  // quando o Last.fm escreveu o artista de um jeito que a iTunes não conhece.
  for (const termo of [`${artista} ${faixa}`.trim(), faixa.trim()]) {
    if (!termo) continue;
    const busca = new URLSearchParams({
      term: termo,
      entity: 'song',
      media: 'music',
      limit: '10',
    });
    const dados = (await pegar(`https://itunes.apple.com/search?${busca}`, 8000)) as Record<
      string,
      unknown
    > | null;
    const resultados = dados?.results;
    if (!Array.isArray(resultados) || resultados.length === 0) continue;

    const alvo = normalizar(faixa);
    const achado =
      (resultados as Record<string, unknown>[]).find(
        (r) => normalizar(`${r.trackName ?? ''}`) === alvo,
      ) ?? (resultados[0] as Record<string, unknown>);

    const capa = `${achado.artworkUrl100 ?? ''}`;
    // A iTunes serve 100×100 na busca; o endereço de 600 existe sempre.
    if (capa) return capa.replace('/100x100bb.jpg', '/600x600bb.jpg');
  }
  return null;
}
